// SnapshotLoader — Phase 5 client-side snapshot fetch + decode pipeline.
//
// Flow (architecture.md §15 / protocol-events.md §2.3):
//   1. fetch(metaUrl)                  → meta.json: { lastSeq, layers }
//   2. fetch(input.layerUrls[i].url)   → PNG blobs (parallel, fresh-signed at welcome)
//      ※ 3〜4 は 1 レイヤーずつ直列 (WebKit canvas メモリ予算対策)
//   3. createImageBitmap(blob)         → ImageBitmap per layer
//   4. drawImage to OffscreenCanvas + getImageData → RGBA Uint8ClampedArray
//   5. return { layers: [{ id, rgba }], meta }
//
// Why `layerUrls` comes from `welcome` / `resume-ok`, not from meta.json:
// - The historical design baked layer presigned URLs into meta.json at
//   publish time. presigned URLs expire (default TTL 60 s for layers),
//   so a client joining > 60 s after the last snapshot publish would
//   pull a meta.json whose layerUrls were already stale and 403 on the
//   layer fetch step. The server now re-signs the per-layer GET URLs
//   on every welcome / resume-ok and ships them in
//   `room.welcome.snapshot.layerUrls` / `room.resume-ok.snapshot.layerUrls`,
//   so they are always fresh at the moment the client tries to fetch.
//   meta.json itself was trimmed to `{ lastSeq, layers }` server-side;
//   this loader takes the input.layerUrls from the welcome/resume-ok
//   payload. (architecture.md §14, protocol-events.md §2.3, see also
//   `snapshotRefSchema` in `@oekaki/protocol`.)
//
// Why decode all the way to RGBA here (instead of handing ImageBitmap to
// the engine):
// - The engine writes pixels via `EngineCore.applySnapshot(rgba)` because
//   the inline / worker rasterizer paths both speak `Uint8ClampedArray`
//   (no canvas API in the worker entry signature). Pushing the decode
//   into the loader keeps `EngineCore` DOM-free.
// - The work happens off the React render path (this is async), so the
//   ~10 ms decode + readback for a 1500x1500 PNG doesn't block input.
//
// Outcome union — exceptions are NEVER thrown from the public surface
// (mirrors `DispatcherClient` style). Caller switches on `outcome`.

import { type SnapshotMeta, snapshotMetaSchema } from '@oekaki/protocol';

import { decodeBitmapToRgba } from '../canvas2d/rasterCanvas.js';
import {
  isSuspiciouslyEmpty,
  type LayerDiagnostic,
  type SnapshotLoadDiagnostics,
} from './snapshotDiagnostics.js';

/**
 * meta.json payload — re-exported alias for the protocol's shared schema.
 *
 * Both the server (`AwsS3SnapshotPublisher`) and this loader parse the
 * same `snapshotMetaSchema` so the wire shape cannot drift between
 * writer and reader. Adding new fields means editing the schema only.
 *
 * Notes:
 *   - `layerUrls` is intentionally NOT here (the loader uses `input.
 *     layerUrls` from `welcome` / `resume-ok` because publish-time
 *     URLs expire after the 60 s TTL — see file header for the bug).
 *   - `background` is carried so the engine can restore the canvas bg
 *     under the layer at display time (2026-05-17 layer/background
 *     separation, docs/_wip/layer-background-separation-2026-05-17.md).
 */
export type { SnapshotMeta };

export interface DecodedSnapshotLayer {
  /** Layer id from `input.layerUrls[i].id` (mirrors `meta.layers[i].id`). */
  readonly id: number;
  /** Decoded pixels in RGBA byte order, length = width * height * 4. */
  readonly rgba: Uint8ClampedArray;
  /** Decoded image dimensions — used by the caller to verify against engine canvas size. */
  readonly width: number;
  readonly height: number;
}

export type SnapshotLoadOutcome =
  | {
      readonly outcome: 'ok';
      readonly meta: SnapshotMeta;
      readonly layers: readonly DecodedSnapshotLayer[];
      /**
       * 2026-06-05 per-layer degradation: fetch / decode に失敗して採用できなかった
       * レイヤー id (= meta.layers に在るが描けなかったもの、または layerUrls に
       * URL が無かったもの). 通常は空 / undefined. 1 枚以上デコードできれば outcome
       * は 'ok' になり、落ちた id がここに載る (= 呼び出し側は console.warn 等の
       * 診断に使える). 全レイヤーが落ちたときは 'ok' ではなく error を返す.
       */
      readonly droppedLayerIds?: readonly number[];
    }
  /** meta.json fetch returned a non-2xx (typically 404 = presigned URL expired). */
  | { readonly outcome: 'error'; readonly cause: 'meta-not-found'; readonly status: number }
  /** Network failure (no Response received). */
  | { readonly outcome: 'error'; readonly cause: 'network'; readonly stage: 'meta' | 'layer' }
  /** Aborted by caller (or timeout). */
  | { readonly outcome: 'error'; readonly cause: 'aborted' }
  /** JSON parse / decode / shape error. */
  | {
      readonly outcome: 'error';
      readonly cause: 'invalid';
      readonly stage: 'meta' | 'layer';
      readonly detail?: string;
    };

export interface SnapshotLoadInput {
  /**
   * Presigned GET URL for `meta.json`. Provided by
   * `room.welcome.snapshot.metaUrl` / `room.resume-ok.snapshot.metaUrl`.
   */
  readonly metaUrl: string;
  /**
   * Fresh presigned GET URLs for each per-layer PNG. Provided by
   * `room.welcome.snapshot.layerUrls` / `room.resume-ok.snapshot.layerUrls`.
   * The server re-signs these on every entry/resume so they are not
   * subject to the meta.json publish-time TTL (see file header). An `id`
   * listed in `meta.layers` that is missing here can't be fetched, so the
   * loader drops it (per-layer degradation, 2026-06-05) and records the id
   * in `droppedLayerIds` rather than failing the whole snapshot.
   */
  readonly layerUrls: ReadonlyArray<{ readonly id: number; readonly url: string }>;
  /**
   * Optional abort signal — the loader hooks it onto every fetch + into
   * the createImageBitmap call. Default: 30s wall-clock timeout via an
   * internal AbortController. Pass `null` to disable the default timeout
   * (tests can hand in their own controller).
   */
  readonly signal?: AbortSignal | null;
  /** Override the default 30 000 ms timeout (ignored when `signal` is provided). */
  readonly timeoutMs?: number;
}

/**
 * Minimal `fetch` shape used by the loader — narrowed so tests can hand
 * in a stub without satisfying the full DOM `fetch`.
 */
export type FetchFn = (input: string, init?: { signal?: AbortSignal }) => Promise<Response>;

/**
 * `createImageBitmap(blob)` shape narrowed for tests. The browser's real
 * one accepts more inputs; we only need the blob form.
 */
export type CreateImageBitmapFn = (blob: Blob) => Promise<ImageBitmap>;

/**
 * Hook used to decode an ImageBitmap into raw RGBA bytes. Browsers
 * accomplish this via `OffscreenCanvas.getContext('2d').getImageData`;
 * tests can pass a stub that returns a hand-built RGBA buffer.
 */
export type DecodeBitmapFn = (bitmap: ImageBitmap) => {
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
};

export interface SnapshotLoaderDeps {
  /** Default: `globalThis.fetch`. */
  readonly fetchFn?: FetchFn;
  /** Default: `globalThis.createImageBitmap`. */
  readonly createImageBitmapFn?: CreateImageBitmapFn;
  /** Default: OffscreenCanvas-based decoder. */
  readonly decodeBitmapFn?: DecodeBitmapFn;
  /**
   * 診断コールバック (aggregate 到達時に 1 回だけ。abort / meta 失敗では呼ばない)。
   * 成功・総失敗どちらでも呼ばれる。throw しても結果に影響しない。
   */
  readonly onDiagnostics?: (d: SnapshotLoadDiagnostics) => void;
  /** 時刻源 (テスト用)。Default: `performance.now()` → `Date.now()`。 */
  readonly nowFn?: () => number;
}

const DEFAULT_TIMEOUT_MS = 30_000;

// Browser path: render onto a work canvas and read back. ~10ms for a 1500x1500
// PNG on a mid-tier laptop, well within the tolerable budget for the welcome /
// reconnect flow.
//
// 2026-08-13 (Sentry WEB-5): 旧実装は **OffscreenCanvas を無条件に要求**して throw
// していた。OffscreenCanvas の 2D は Safari 16.4 で初めて載ったので、**iOS 16.3 以前
// では必ず失敗**し、途中入室したユーザーに絵が出ない。ここはメインスレッド
// (`SessionProvider`) から呼ばれるので `<canvas>` で代替できる → `rasterCanvas` へ委譲。
const defaultDecodeBitmap: DecodeBitmapFn = (bitmap) => decodeBitmapToRgba(bitmap);

/**
 * Look up a `fetch` implementation. Tests injecting a stub satisfy this
 * trivially; in the browser we forward `globalThis.fetch` (binding it so
 * `this` is correct).
 */
const defaultFetchFn = (input: string, init?: { signal?: AbortSignal }): Promise<Response> => {
  const f = globalThis.fetch as
    | ((url: string, init?: RequestInit) => Promise<Response>)
    | undefined;
  if (f === undefined) throw new Error('globalThis.fetch is not available');
  return f.call(globalThis, input, init as RequestInit | undefined);
};

const defaultCreateImageBitmap: CreateImageBitmapFn = (blob) => {
  const f = globalThis.createImageBitmap as ((b: Blob) => Promise<ImageBitmap>) | undefined;
  if (f === undefined) throw new Error('globalThis.createImageBitmap is not available');
  return f.call(globalThis, blob);
};

const isAbortError = (cause: unknown): boolean => {
  if (cause instanceof Error && cause.name === 'AbortError') return true;
  // Some runtimes throw plain DOMException objects.
  if (
    typeof cause === 'object' &&
    cause !== null &&
    (cause as { name?: unknown }).name === 'AbortError'
  ) {
    return true;
  }
  return false;
};

/**
 * Parse a raw meta.json payload via the shared Zod schema. Returns the
 * parsed shape (with defaults filled in — e.g. older snapshots that
 * predate the `background` field round-trip as bg-white) or `null` on
 * shape error.
 *
 * `layerUrls` is intentionally NOT validated here — the loader uses
 * `input.layerUrls` (fresh-signed at welcome/resume-ok time) and ignores
 * any `layerUrls` field that older meta.json files may still carry.
 * The schema is `loose()` so extra fields don't reject the payload.
 */
const tryParseMeta = (raw: unknown): SnapshotMeta | null => {
  const parsed = snapshotMetaSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

/**
 * Fetch + decode a snapshot. Returns an outcome union — never throws on
 * expected failure paths (network / 404 / decode / abort). Programming
 * errors (e.g. `OffscreenCanvas` unavailable in a node test that didn't
 * pass `decodeBitmapFn`) DO throw — those indicate misconfiguration.
 */
export const loadSnapshot = async (
  input: SnapshotLoadInput,
  deps: SnapshotLoaderDeps = {},
): Promise<SnapshotLoadOutcome> => {
  const fetchFn = deps.fetchFn ?? defaultFetchFn;
  const createBitmap = deps.createImageBitmapFn ?? defaultCreateImageBitmap;
  const decodeBitmap = deps.decodeBitmapFn ?? defaultDecodeBitmap;
  const now =
    deps.nowFn ??
    ((): number => (typeof performance !== 'undefined' ? performance.now() : Date.now()));

  // Build the abort signal: caller-provided wins; otherwise wrap in a
  // timeout. A `null` explicit signal disables the default.
  let signal: AbortSignal | undefined;
  let timer: ReturnType<typeof setTimeout> | null = null;
  if (input.signal === null) {
    signal = undefined;
  } else if (input.signal !== undefined) {
    signal = input.signal;
  } else {
    const controller = new AbortController();
    timer = setTimeout(() => controller.abort(), input.timeoutMs ?? DEFAULT_TIMEOUT_MS);
    signal = controller.signal;
  }
  const cleanupTimer = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  // ─── 1. Fetch + parse meta.json ──────────────────────────────────────────
  let metaRes: Response;
  try {
    const init = signal === undefined ? undefined : { signal };
    metaRes = await fetchFn(input.metaUrl, init);
  } catch (cause) {
    cleanupTimer();
    if (isAbortError(cause)) return { outcome: 'error', cause: 'aborted' };
    return { outcome: 'error', cause: 'network', stage: 'meta' };
  }
  if (!metaRes.ok) {
    cleanupTimer();
    return { outcome: 'error', cause: 'meta-not-found', status: metaRes.status };
  }

  let rawMeta: unknown;
  try {
    rawMeta = await metaRes.json();
  } catch (cause) {
    cleanupTimer();
    if (isAbortError(cause)) return { outcome: 'error', cause: 'aborted' };
    return {
      outcome: 'error',
      cause: 'invalid',
      stage: 'meta',
      detail: 'meta.json parse failed',
    };
  }
  const meta = tryParseMeta(rawMeta);
  if (meta === null) {
    cleanupTimer();
    return {
      outcome: 'error',
      cause: 'invalid',
      stage: 'meta',
      detail: 'meta.json shape mismatch',
    };
  }

  // ─── 2. Fetch layers in parallel, decode them sequentially ────────────────────────────
  // Sort by id so the caller can rely on base layer being first (β layer
  // composition is order-sensitive; MVP has just one layer so this is a
  // trivial pass). The URLs come from `input.layerUrls` (welcome / resume-ok)
  // because they are fresh-signed at entry time — see file header for why.
  //
  // 2026-06-05 per-layer degradation: meta.layers に在るが input.layerUrls に
  // URL が無いレイヤーは fetch しようがないので、ここで「落ちた id」として記録
  // して飛ばす (旧実装はこれを `invalid` で全体失敗にしていた — 報告参照).
  const inputLayerIds = new Set(input.layerUrls.map((u) => u.id));
  const metaOnlyMissingIds = meta.layers
    .filter((layer) => !inputLayerIds.has(layer.id))
    .map((layer) => layer.id);
  const sortedUrls = [...input.layerUrls].sort((a, b) => a.id - b.id);

  type LayerLoadError = Extract<SnapshotLoadOutcome, { outcome: 'error' }>;
  type LayerResult = DecodedSnapshotLayer | { error: LayerLoadError; id: number };

  // fetch (PNG の blob 取得) は並列のまま (圧縮データなので軽く、RTT が重なる)。
  // **デコードだけ**を 1 枚ずつ直列に行う (下の for ループ): 2〜5 枚の W×H×4 byte の
  // 作業 canvas + ImageData が同時に生きると WebKit の canvas メモリ予算を食い、
  // 無音で透明ピクセル / null context になりうるため。デコードは数十 ms/枚なので
  // 典型の 2〜3 枚では直列化による遅延は誤差。
  type BlobResult = { blob: Blob } | { error: LayerLoadError };
  const fetchBlob = async (entry: { id: number; url: string }): Promise<BlobResult> => {
    let res: Response;
    try {
      const init = signal === undefined ? undefined : { signal };
      res = await fetchFn(entry.url, init);
    } catch (cause) {
      if (isAbortError(cause)) return { error: { outcome: 'error', cause: 'aborted' } };
      return { error: { outcome: 'error', cause: 'network', stage: 'layer' } };
    }
    if (!res.ok) {
      return {
        error: {
          outcome: 'error',
          cause: 'invalid',
          stage: 'layer',
          detail: `layer ${entry.id} fetch returned ${res.status}`,
        },
      };
    }
    try {
      return { blob: await res.blob() };
    } catch (cause) {
      if (isAbortError(cause)) return { error: { outcome: 'error', cause: 'aborted' } };
      return {
        error: {
          outcome: 'error',
          cause: 'invalid',
          stage: 'layer',
          detail: `layer ${entry.id} blob read failed`,
        },
      };
    }
  };

  const decodeLayer = async (id: number, blob: Blob): Promise<LayerResult> => {
    let bitmap: ImageBitmap;
    try {
      bitmap = await createBitmap(blob);
    } catch (cause) {
      if (isAbortError(cause)) {
        return { error: { outcome: 'error', cause: 'aborted' }, id };
      }
      return {
        error: {
          outcome: 'error',
          cause: 'invalid',
          stage: 'layer',
          detail: `layer ${id} createImageBitmap failed`,
        },
        id,
      };
    }
    let decoded: { rgba: Uint8ClampedArray; width: number; height: number };
    try {
      decoded = decodeBitmap(bitmap);
    } catch (cause) {
      // 2026-08-13 (Sentry WEB-5): ここだけ catch が無く、decode の失敗が
      // `loadSnapshot` の外まで飛んでいた。他の全段 (fetch / blob /
      // createImageBitmap) は per-layer degradation に倒しているのに、decode だけが
      // 「1 枚失敗したら snapshot 全体を捨てる」どころか **公開契約 (= expected
      // failure では throw しない、上の doc コメント参照) ごと破っていた**。
      // 呼び出し側は fire-and-forget なので、throw は unhandled rejection になり
      // ユーザーには「読み込んでいます…」のまま何も出ない。他段と同じ形に揃える。
      if (isAbortError(cause)) {
        return { error: { outcome: 'error', cause: 'aborted' }, id };
      }
      return {
        error: {
          outcome: 'error',
          cause: 'invalid',
          stage: 'layer',
          detail: `layer ${id} decode failed`,
        },
        id,
      };
    } finally {
      // Release the bitmap's native memory ASAP. `close()` is safe to
      // call even if `decodeBitmap` already consumed the bitmap.
      try {
        bitmap.close?.();
      } catch {
        // ignore
      }
    }
    return { id, rgba: decoded.rgba, width: decoded.width, height: decoded.height };
  };

  const results: LayerResult[] = [];
  const layerDiagnostics: LayerDiagnostic[] = [];
  const decodeStart = now();
  try {
    // fetch は全レイヤー並列で先行開始 (BlobResult は reject しないので未処理 rejection なし)。
    const blobPromises = sortedUrls.map((entry) => fetchBlob(entry));
    // デコードは 1 枚ずつ。
    for (const [i, entry] of sortedUrls.entries()) {
      const fetched = await (blobPromises[i] as Promise<BlobResult>);
      if ('error' in fetched) {
        results.push({ error: fetched.error, id: entry.id });
        layerDiagnostics.push({ id: entry.id });
        continue;
      }
      const t0 = now();
      const r = await decodeLayer(entry.id, fetched.blob);
      const decodeMs = now() - t0;
      if ('error' in r) {
        results.push(r);
        layerDiagnostics.push({ id: entry.id, pngBytes: fetched.blob.size, decodeMs });
        continue;
      }
      results.push(r);
      layerDiagnostics.push({
        id: entry.id,
        pngBytes: fetched.blob.size,
        width: r.width,
        height: r.height,
        decodeMs,
        // 1 回だけの安価な判定 (小さい PNG は byte 数比較で走査自体を省く)。
        suspiciouslyEmpty: isSuspiciouslyEmpty(fetched.blob.size, r.width, r.height, r.rgba),
      });
    }
  } finally {
    cleanupTimer();
  }
  const totalDecodeMs = now() - decodeStart;

  // ─── 3. Aggregate — per-layer degradation (2026-06-05) ───────────────────
  // 旧実装は "first error wins" で、1 枚でも fetch / decode に失敗すると snapshot
  // 全体を破棄していた. その結果、レイヤー 1(OK) / 2(NG) / 3(OK) のとき 2 の
  // あおりで 1・3 まで描画されない事故が起きた (報告 2026-06-05; incremental
  // snapshot 化前の古いルームで cross-gen layer が 404 する等). ここでは
  // デコードできたレイヤーだけ採用し、落ちたレイヤーは id を記録して飛ばす.
  //   - `aborted` だけは「読み込み自体の中断」なので全体を中断扱いで即返す
  //     (= 部分適用しない. 上書きする新しい読み込み / 離脱に任せる).
  //   - 1 枚も採用できず、かつ何らかのエラーがあったときだけ、最初の非 abort
  //     エラーを総失敗として返す (= 呼び出し側の穏やかな「一部の既存の絵は
  //     読み込めませんでした」表示に倒す).
  const decoded: DecodedSnapshotLayer[] = [];
  const droppedLayerIds: number[] = [...metaOnlyMissingIds];
  let firstError: LayerLoadError | null = null;
  for (const r of results) {
    if ('error' in r) {
      if (r.error.cause === 'aborted') return r.error;
      droppedLayerIds.push(r.id);
      if (firstError === null) firstError = r.error;
      continue;
    }
    decoded.push(r);
  }

  // 1 枚も採用できなかった = 実質「絵が無い」ので総失敗として返す. ただし
  // 採用 0 でもエラーが 1 つも無い (= そもそも layerUrls が空) ケースは、
  // 「空の snapshot を正常に読んだ」とみなして ok (layers=[]) を返す.
  if (deps.onDiagnostics !== undefined) {
    try {
      deps.onDiagnostics({
        layerCount: meta.layers.length,
        layers: layerDiagnostics,
        droppedLayerIds,
        totalDecodeMs,
      });
    } catch {
      // 診断は結果に影響させない
    }
  }

  if (decoded.length === 0 && firstError !== null) {
    return firstError;
  }

  return {
    outcome: 'ok',
    meta,
    layers: decoded,
    ...(droppedLayerIds.length > 0 ? { droppedLayerIds } : {}),
  };
};
